/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState, useMemo } from 'react';
import { uniqBy } from 'lodash';
import { getWorkspaceId, useAppSelector } from '@zonofi/zono-money-store';

interface UsePaginatedListProps<T> {
  fetchDataApiCall: (params: { pageNo: number; pageSize: number }) => void;
  apiResponseData: T[];
  isLoading: boolean;
  isError?: boolean;
  uniqueKey: keyof T;
  dependencies?: any[];
  pageSize?: number;
  paginationMeta?: {
    startRecord: number;
    endRecord: number;
    totalCount: number;
  };
}

const DEFAULT_PAGE_SIZE = 10;

export const usePaginatedList = <T>({
  fetchDataApiCall,
  apiResponseData = [],
  isLoading = false,
  isError = false,
  uniqueKey,
  dependencies = [],
  pageSize = DEFAULT_PAGE_SIZE,
  paginationMeta,
}: UsePaginatedListProps<T>) => {
  const [data, setData] = useState<T[]>([]);
  const [pageNo, setPageNo] = useState(1);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);

  const lastItemRef = useRef<HTMLDivElement | null>(null);
  const fetchedPagesRef = useRef<Set<number>>(new Set());
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const hasMoreDataRef = useRef(true);
  const paginationLock = useRef(false);

  const combinedDeps = useMemo(
    () => [sellerWorkspaceId, ...dependencies],
    [sellerWorkspaceId, ...dependencies]
  );

  const onScroll = () => {
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

    scrollTimeout.current = setTimeout(() => {
      const node = lastItemRef.current;
      const justReset = pageNo === 1 && data.length === 0;

      if (
        node &&
        !isLoading &&
        !isPageLoading &&
        !justReset &&
        !paginationLock.current &&
        hasMoreDataRef.current &&
        !fetchedPagesRef.current.has(pageNo + 1)
      ) {
        const rect = node.getBoundingClientRect();
        const reachedBottom = rect.bottom - 50 <= window.innerHeight;
        if (
          reachedBottom ||
          (pageSize !== DEFAULT_PAGE_SIZE &&
            paginationMeta &&
            pageSize * pageNo >= paginationMeta.totalCount)
        ) {
          paginationLock.current = true;
          setIsPageLoading(true);
          setPageNo((prev) => prev + 1);
        }
      }
    }, 200);
  };

  useEffect(() => {
    resetAll();
    fetchDataApiCall({ pageNo: 1, pageSize });
  }, [...combinedDeps]);

  useEffect(() => {
    if (
      !fetchedPagesRef.current.has(pageNo) &&
      hasMoreDataRef.current &&
      !isError &&
      !isLoading
    ) {
      fetchDataApiCall({ pageNo, pageSize });
    }
  }, [pageNo]);

  const checkAndLoadMoreData = () => {
    const node = lastItemRef.current;
    if (
      node &&
      !isLoading &&
      !isPageLoading &&
      !paginationLock.current &&
      hasMoreDataRef.current &&
      !fetchedPagesRef.current.has(pageNo + 1)
    ) {
      const rect = node.getBoundingClientRect();
      const isContentBelowViewport = rect.bottom > window.innerHeight;

      if (
        !isContentBelowViewport ||
        (pageSize !== DEFAULT_PAGE_SIZE &&
          paginationMeta &&
          paginationMeta.endRecord < paginationMeta.totalCount)
      ) {
        paginationLock.current = true;
        setIsPageLoading(true);
        setPageNo((prev) => prev + 1);
      }
    }
  };

  useEffect(() => {
    if (!isLoading && !isError && apiResponseData) {
      setData((prevData) => {
        const merged =
          pageNo === 1 ? apiResponseData : [...prevData, ...apiResponseData];
        return uniqBy(merged, uniqueKey as string);
      });

      fetchedPagesRef.current.add(pageNo);

      if (paginationMeta) {
        const { endRecord, totalCount } = paginationMeta;
        hasMoreDataRef.current = endRecord < totalCount;
      } else {
        hasMoreDataRef.current = apiResponseData.length >= pageSize;
      }

      setIsPageLoading(false);
      paginationLock.current = false;

      setTimeout(() => {
        checkAndLoadMoreData();
      }, 100);
    } else if (isError) {
      setIsPageLoading(false);
      paginationLock.current = false;
      hasMoreDataRef.current = false;
    }
  }, [apiResponseData, isLoading, isError, pageNo]);

  const resetAll = () => {
    setPageNo(1);
    setIsPageLoading(false);
    fetchedPagesRef.current.clear();
    hasMoreDataRef.current = true;
    paginationLock.current = false;
  };

  const hasMoreData = hasMoreDataRef.current;

  return {
    data,
    pageNo,
    lastItemRef,
    onScroll,
    resetAll,
    isPageLoading,
    hasMoreData,
    isEndOfList: !hasMoreData,
  };
};
